import {
  ChevronRight,
  MoreHorizontal,
  RotateCcw,
  Slash,
  Users,
  XCircle
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { ListCard, ListRow } from "@/components/ui/list-card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { lawyerSelectors } from "../../redux/lawyer/selectors";
import {
  LawyerRole,
  type LawFirm,
  type LawyerDTO
} from "../../api/types/lawFirm";
import { UsersAPI } from "../../api/usersApi";
import { LawyerAPI } from "../../api/lawyerApi";
import { LawFirmsAPI } from "../../api/lawFirms";

const getLawyerName = (lawyer: LawyerDTO) => {
  return [lawyer.firstName, lawyer.middleName, lawyer.lastName]
    .filter((part) => typeof part === "string" && part.trim())
    .join(" ")
    .trim();
};

const formatRoleLabel = (role?: string | null) => {
  const normalized = String(role || "")
    .trim()
    .replace(/[_-]+/g, " ");

  if (!normalized) return "Unassigned";

  return normalized
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

const EDITABLE_ROLE_OPTIONS: LawyerRole[] = Object.values(LawyerRole).filter(
  (role) =>
    role !== LawyerRole.SUPERADMIN &&
    role !== LawyerRole.FULL &&
    role !== LawyerRole.STANDARD
);

const getInitials = (label: string) => {
  const parts = label
    .split(" ")
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return `${parts[0].slice(0, 1).toUpperCase()}${parts[parts.length - 1]
    .slice(0, 1)
    .toUpperCase()}`;
};

type Props = {
  lawFirmId: string | null;
  manageableLawFirms?: LawFirm[];
};

type RoleSelectProps = {
  value?: string;
  placeholder: string;
  disabled?: boolean;
  onValueChange: (value: string) => void;
};

function RoleSelect({
  value,
  placeholder,
  disabled = false,
  onValueChange
}: RoleSelectProps) {
  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger
        size="sm"
        aria-label="Role"
        className="w-30 justify-end border-transparent text-fg-2"
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent align="end">
        {EDITABLE_ROLE_OPTIONS.map((role) => (
          <SelectItem key={role} value={role}>
            {formatRoleLabel(role)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

type FirmSelectProps = {
  value: string;
  firms: LawFirm[];
  onValueChange: (value: string) => void;
};

function TeamBreadcrumb({ value, firms, onValueChange }: FirmSelectProps) {
  const selectedFirm = firms.find((firm) => firm.uid === value);
  const hasMultipleFirms = firms.length > 1;

  return (
    <div className="flex w-full min-w-0 items-center gap-1.5 text-xl font-medium text-fg">
      <h2>Team</h2>
      {selectedFirm ? (
        <>
          <ChevronRight className="size-4 shrink-0 text-fg-4" />
          {hasMultipleFirms ? (
            <Select value={value} onValueChange={onValueChange}>
              <SelectTrigger
                aria-label="Law Firm"
                className="h-8 min-w-0 border-transparent px-1.5 text-xl font-medium"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {firms.map((firm) => (
                  <SelectItem key={firm.uid} value={firm.uid}>
                    {firm.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="truncate">{selectedFirm.name}</span>
          )}
        </>
      ) : null}
    </div>
  );
}

export function TeamTab({ lawFirmId, manageableLawFirms = [] }: Props) {
  const currentUserId = useSelector(lawyerSelectors.selectUid);
  const [selectedLawFirmId, setSelectedLawFirmId] = useState<string | null>(
    lawFirmId
  );
  const [teamMembers, setTeamMembers] = useState<LawyerDTO[]>([]);
  const [avatarUrls, setAvatarUrls] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [savingRoleId, setSavingRoleId] = useState<string | null>(null);
  const [togglingMemberId, setTogglingMemberId] = useState<string | null>(null);

  const firmOptions = useMemo(() => {
    const byId = new Map<string, LawFirm>();
    manageableLawFirms.forEach((firm) => {
      if (firm.uid) byId.set(firm.uid, firm);
    });
    return Array.from(byId.values());
  }, [manageableLawFirms]);

  const selectedFirmId = useMemo(() => {
    const validFirmIds = new Set([
      lawFirmId,
      ...firmOptions.map((firm) => firm.uid)
    ]);
    if (selectedLawFirmId && validFirmIds.has(selectedLawFirmId)) {
      return selectedLawFirmId;
    }
    return lawFirmId;
  }, [firmOptions, lawFirmId, selectedLawFirmId]);

  useEffect(() => {
    setSelectedLawFirmId((current) => {
      if (
        current &&
        (current === lawFirmId ||
          firmOptions.some((firm) => firm.uid === current))
      ) {
        return current;
      }
      return lawFirmId;
    });
  }, [firmOptions, lawFirmId]);

  const renderTeamHeader = () => {
    if (!selectedFirmId) {
      return <h2 className="text-xl font-medium text-fg">Team</h2>;
    }

    return (
      <TeamBreadcrumb
        value={selectedFirmId}
        firms={firmOptions}
        onValueChange={setSelectedLawFirmId}
      />
    );
  };

  useEffect(() => {
    let cancelled = false;

    if (!selectedFirmId) {
      setTeamMembers([]);
      setAvatarUrls({});
      setHasError(false);
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const run = async () => {
      try {
        setIsLoading(true);
        setHasError(false);
        setAvatarUrls({});
        const lawyers = await LawFirmsAPI.listLawyers(selectedFirmId);
        const nextTeamMembers = Array.isArray(lawyers) ? lawyers : [];

        if (cancelled) return;
        setTeamMembers(nextTeamMembers);
        setIsLoading(false);

        nextTeamMembers.forEach((lawyer) => {
          const userId = (lawyer.uid ?? "").trim();
          if (!userId) return;

          UsersAPI.getProfile(userId)
            .then((profile) => {
              if (cancelled) return;
              const avatarUrl = profile.avatar_data_url ?? "";
              if (!avatarUrl) return;
              setAvatarUrls((prev) => ({
                ...prev,
                [userId]: avatarUrl
              }));
            })
            .catch(() => {
              // Avatar loading is best-effort; keep initials if a profile request fails.
            });
        });
      } catch {
        if (cancelled) return;
        setHasError(true);
        setTeamMembers([]);
        setAvatarUrls({});
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [selectedFirmId]);

  const sortedTeamMembers = useMemo(() => {
    return [...teamMembers].sort((a, b) => {
      if (a.uid && a.uid === currentUserId) return -1;
      if (b.uid && b.uid === currentUserId) return 1;

      const aDisabled = Boolean(a.disabled);
      const bDisabled = Boolean(b.disabled);
      if (aDisabled !== bDisabled) return aDisabled ? 1 : -1;

      const aLabel = getLawyerName(a) || a.email || "Unknown";
      const bLabel = getLawyerName(b) || b.email || "Unknown";
      return aLabel.localeCompare(bLabel, undefined, { sensitivity: "base" });
    });
  }, [currentUserId, teamMembers]);

  const handleRoleChange = async (member: LawyerDTO, nextRole: string) => {
    const lawyerId = (member.uid ?? "").trim();
    const currentRole = String(
      member.role ?? member.lawyerRole ?? member.lawyer_role ?? ""
    )
      .trim()
      .toLowerCase();

    if (!lawyerId) return;
    if (!nextRole || nextRole === currentRole) return;
    if (currentRole === LawyerRole.SUPERADMIN) return;

    setSavingRoleId(lawyerId);
    try {
      const updatedLawyer = await LawyerAPI.update(lawyerId, {
        role: nextRole
      });
      setTeamMembers((prev) =>
        prev.map((existing) =>
          existing.uid === lawyerId
            ? { ...existing, ...updatedLawyer }
            : existing
        )
      );
      toast.success("Role updated", {
        description: `${
          getLawyerName(updatedLawyer) || updatedLawyer.email || "Team member"
        } is now ${formatRoleLabel(nextRole)}.`
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to update role.";
      toast.error("Couldn't update role", { description: message });
    } finally {
      setSavingRoleId(null);
    }
  };

  const handleDisabledChange = async (
    member: LawyerDTO,
    nextDisabled: boolean
  ) => {
    const lawyerId = (member.uid ?? "").trim();
    const displayName = getLawyerName(member) || member.email || "Team member";
    const rawRole = String(
      member.role ?? member.lawyerRole ?? member.lawyer_role ?? ""
    )
      .trim()
      .toLowerCase();

    if (!lawyerId) return;
    if (lawyerId === currentUserId) return;
    if (rawRole === LawyerRole.SUPERADMIN) return;

    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      nextDisabled
        ? `Disable ${displayName}? They will no longer be able to sign in.`
        : `Re-enable ${displayName}? They will be able to sign in again.`
    );
    if (!confirmed) return;

    setTogglingMemberId(lawyerId);
    try {
      const updatedLawyer = nextDisabled
        ? await LawyerAPI.disable(lawyerId)
        : await LawyerAPI.enable(lawyerId);

      setTeamMembers((prev) =>
        prev.map((existing) =>
          existing.uid === lawyerId
            ? { ...existing, ...updatedLawyer }
            : existing
        )
      );

      toast.success(nextDisabled ? "User disabled" : "User enabled", {
        description: nextDisabled
          ? `${displayName} can no longer sign in to this law firm.`
          : `${displayName} can sign in to this law firm again.`
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : nextDisabled
          ? "Unable to disable user."
          : "Unable to enable user.";
      toast.error(
        nextDisabled ? "Couldn't disable user" : "Couldn't enable user",
        { description: message }
      );
    } finally {
      setTogglingMemberId(null);
    }
  };

  const shell = (body: ReactNode) => (
    <div className="flex w-full flex-col gap-6 px-6 pt-5 pb-8">
      {renderTeamHeader()}
      {body}
    </div>
  );

  if (!lawFirmId) {
    return shell(
      <EmptyState
        icon={Users}
        title="No Law Firm"
        description="No law firm is associated with this account."
      />
    );
  }

  if (isLoading) {
    return shell(
      <ListCard data-testid="team-loading">
        {[0, 1, 2].map((item) => (
          <div key={item} className="flex items-center gap-3 px-4 py-3">
            <Skeleton className="size-8 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3 w-40" />
              <Skeleton className="h-2.5 w-48" />
            </div>
            <Skeleton className="h-5 w-20" />
          </div>
        ))}
      </ListCard>
    );
  }

  if (hasError) {
    return shell(
      <EmptyState
        icon={Users}
        title="Couldn't Load Your Team"
        description="Unable to load your team right now."
      />
    );
  }

  if (sortedTeamMembers.length === 0) {
    return shell(
      <EmptyState
        icon={Users}
        title="No Team Members"
        description="No team members found for this firm."
      />
    );
  }

  return shell(
    <ListCard>
      {sortedTeamMembers.map((member) => {
        const baseName = getLawyerName(member) || member.email || "Unknown";
        const displayName = member.uid === currentUserId ? "You" : baseName;
        const displayRole =
          member.role ?? member.lawyerRole ?? member.lawyer_role ?? null;
        const roleLabel = formatRoleLabel(displayRole);
        const avatarLabel = getLawyerName(member) || member.email || "Unknown";
        const avatarSrc = avatarUrls[(member.uid ?? "").trim()] || undefined;
        const rawRole = String(displayRole || "")
          .trim()
          .toLowerCase();
        const lawyerId = (member.uid ?? "").trim();
        const isSelf = Boolean(lawyerId && lawyerId === currentUserId);
        const isDisabled = Boolean(member.disabled);
        const isSuperadmin = rawRole === LawyerRole.SUPERADMIN;
        const isSavingRole = savingRoleId === lawyerId;
        const isTogglingMember = togglingMemberId === lawyerId;
        const hasEditableRoleOption = EDITABLE_ROLE_OPTIONS.includes(
          rawRole as LawyerRole
        );
        const canToggleStatus = Boolean(lawyerId && !isSelf && !isSuperadmin);

        let status: ReactNode;
        if (isTogglingMember) {
          status = (
            <span className="text-base text-fg-3">
              {isDisabled ? "Enabling…" : "Disabling…"}
            </span>
          );
        } else if (isDisabled) {
          status = (
            <span className="flex items-center gap-1 text-base text-fg-3">
              <XCircle className="size-3.5" />
              Disabled
            </span>
          );
        } else if (isSuperadmin) {
          status = <span className="text-base text-fg-3">{roleLabel}</span>;
        } else {
          status = (
            <RoleSelect
              value={hasEditableRoleOption ? rawRole : undefined}
              onValueChange={(value) => handleRoleChange(member, value)}
              disabled={isSavingRole || isSelf}
              placeholder={hasEditableRoleOption ? "Select role" : roleLabel}
            />
          );
        }

        return (
          <ListRow
            key={member.uid || `${member.email}-${displayName}`}
            media={
              <Avatar>
                {avatarSrc ? <AvatarImage src={avatarSrc} alt="" /> : null}
                <AvatarFallback>{getInitials(avatarLabel)}</AvatarFallback>
              </Avatar>
            }
            title={displayName}
            description={
              <span className="truncate text-sm text-fg-3">
                {member.email || "—"}
              </span>
            }
            control={
              <div className="flex items-center gap-2">
                {status}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={
                        isTogglingMember
                          ? isDisabled
                            ? "Enabling user"
                            : "Disabling user"
                          : "Member actions"
                      }
                      disabled={!canToggleStatus || isTogglingMember}
                    >
                      {isTogglingMember ? <Spinner /> : <MoreHorizontal />}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      variant={isDisabled ? "default" : "destructive"}
                      disabled={!canToggleStatus || isTogglingMember}
                      onSelect={() => handleDisabledChange(member, !isDisabled)}
                    >
                      {isDisabled ? <RotateCcw /> : <Slash />}
                      {isDisabled ? "Enable User" : "Disable User"}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            }
          />
        );
      })}
    </ListCard>
  );
}
